import {
    Colors,
    ConfirmDialog,
    ICON_STROKE_WIDTHS,
    Icon,
    SelectFilled,
    Text,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
    type OptionProps,
} from '@amelio/ui-library';
import { useMemo, useState } from 'react';

import type { Competency } from '@/types/competency/Competency';

import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { getTextByLanguage } from '@/utils/localization';

type GroupActionMode = 'delete' | 'deactivate';

export type GroupActionModalProps = {
    /** The page mounts this dialog only while a group action is pending, so it opens by default. */
    isOpen?: boolean;
    mode: GroupActionMode;
    groupId: string;
    groupName: string;
    competencies: Competency[];
    replacementOptions: OptionProps[];
    isSubmitting: boolean;
    onClose: () => void;
    onConfirm: (replacements: Record<string, string | undefined>) => void;
};

const NO_REPLACEMENT = '';

const COUNT_BADGE_CLASSES = {
    active: 'bg-primary/10 text-primary',
    inactive: 'bg-gray-100 text-gray-500',
};

export const GroupActionModal = ({
    isOpen = true,
    mode,
    groupName,
    competencies,
    replacementOptions,
    isSubmitting,
    onClose,
    onConfirm,
}: GroupActionModalProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();

    const [replacements, setReplacements] = useState<Record<string, string>>({});

    const isDelete = mode === 'delete';
    const namespace = isDelete ? 'deleteGroup' : 'deactivateGroup';

    const selectOptions = useMemo<OptionProps[]>(
        () => [
            {
                id: NO_REPLACEMENT,
                label: t(`competencies.${namespace}.noReplacement`),
                value: NO_REPLACEMENT,
            },
            ...replacementOptions,
        ],
        [replacementOptions, t, namespace],
    );

    const { affectedCompetencies, totalAssignments } = useMemo(() => {
        const counts = competencies.map((competency) => competency.assignedEmployeesCount ?? 0);

        return {
            totalAssignments: counts.reduce((sum, count) => sum + count, 0),
            affectedCompetencies: counts.filter((count) => count > 0).length,
        };
    }, [competencies]);

    const handleConfirm = () => {
        const payload = Object.entries(replacements).reduce<Record<string, string | undefined>>(
            (acc, [competencyId, replacementId]) => {
                acc[competencyId] = replacementId || undefined;
                return acc;
            },
            {},
        );

        onConfirm(payload);
    };

    const titleText = t(`competencies.${namespace}.title`, { name: groupName });
    // ConfirmDialog types `title` as a string but renders it as a node inside its Heading, so the
    // leading icon rides along with the text (same icons as CascadeModalHeader).
    const title = (
        <span className={'mb-[4px] flex items-center gap-2'}>
            <Icon
                name={isDelete ? 'essential-trash' : 'electronics-power-off'}
                size={'md'}
                color={isDelete ? Colors.ERROR : Colors.BASE_400}
                strokeWidth={ICON_STROKE_WIDTHS.bold}
                ariaHidden
                className={'shrink-0'}
                dataTestId={'group-action-modal-title-icon'}
            />

            <span className={'text-body-lg'}>{titleText}</span>
        </span>
    ) as unknown as string;
    const warning = t(`competencies.${namespace}.warning`);
    const confirmLabel = t(`competencies.${namespace}.confirm`);

    const competenciesText = t('competencies.competencyCount', { count: competencies.length });
    const assignedText = t(`competencies.${namespace}.assignedCompetencies`, {
        count: affectedCompetencies,
    });
    const assignmentsText = t(`competencies.${namespace}.totalAssignments`, {
        count: totalAssignments,
    });

    const hasNoCompetencies = competencies.length === 0;

    // Same alertdialog contract as the library ConfirmDialog, built on Modal so this dialog owns its
    // footer: md buttons sized to their label, and a gradient confirm when the action is not a delete.
    return (
        <ConfirmDialog
            isOpen={isOpen}
            title={title}
            confirmLabel={confirmLabel}
            cancelLabel={t('common.cancel')}
            onConfirm={handleConfirm}
            onCancel={onClose}
            isLoading={isSubmitting}
            isDestructive={isDelete}
            className={'w-168 max-w-full'}
            dataTestId={
                isDelete ? 'competency-delete-group-modal' : 'competency-deactivate-group-modal'
            }
            message={
                <div className={'flex flex-col gap-4'}>
                    <div
                        className={`flex flex-col gap-3 rounded-lg border p-4 ${isDelete ? 'border-error/20 bg-error/10' : 'border-warning/50 bg-warning/10'}`}
                    >
                        <Text
                            size={'small'}
                            weight={'medium'}
                        >
                            <span
                                className={`flex items-start gap-2 ${isDelete ? 'text-error' : 'text-warning'}`}
                            >
                                <Icon
                                    name={'essential-danger'}
                                    size={'md'}
                                    color={'inherit'}
                                    ariaHidden
                                    className={'shrink-0'}
                                />

                                <span>{warning}</span>
                            </span>
                        </Text>

                        {!hasNoCompetencies && (
                            <Text size={'small'}>
                                <span className={'text-navy'}>
                                    {t(`competencies.${namespace}.impactSummary`, {
                                        competencies: competenciesText,
                                        assigned: assignedText,
                                        assignments: assignmentsText,
                                    })}
                                </span>
                            </Text>
                        )}
                    </div>

                    {!hasNoCompetencies && (
                        <div className={'overflow-hidden rounded-lg border border-gray-200'}>
                            <div
                                className={
                                    'grid grid-cols-12 gap-3 bg-gray-50 px-4 py-2 text-xs font-medium tracking-wide text-gray-500 uppercase'
                                }
                            >
                                <div className={'col-span-4'}>
                                    {t('competencies.deleteGroup.competency')}
                                </div>
                                <div className={'col-span-3 text-center'}>
                                    {t('competencies.deleteGroup.employees')}
                                </div>
                                <div className={'col-span-5'}>
                                    {t('competencies.deleteGroup.replaceWith')}
                                </div>
                            </div>

                            <div className={'max-h-80 divide-y divide-gray-200 overflow-y-auto'}>
                                {competencies.map((competency) => {
                                    const count = competency.assignedEmployeesCount ?? 0;
                                    const badgeClass =
                                        count > 0
                                            ? COUNT_BADGE_CLASSES.active
                                            : COUNT_BADGE_CLASSES.inactive;

                                    return (
                                        <div
                                            key={competency.id}
                                            className={
                                                'grid grid-cols-12 items-center gap-3 px-4 py-3'
                                            }
                                        >
                                            <div className={'text-navy col-span-4 text-sm'}>
                                                {getTextByLanguage(competency.name, language)}
                                            </div>

                                            <div className={'col-span-3 text-center'}>
                                                <span
                                                    className={`inline-flex h-6 min-w-8 items-center justify-center rounded-full px-2 text-sm font-medium ${badgeClass}`}
                                                >
                                                    {count}
                                                </span>
                                            </div>

                                            <div className={'col-span-5'}>
                                                {/* A replacement is only offered when somebody actually holds the competency; the
                                                    tooltip says why the control is inert rather than leaving a dead dropdown. */}
                                                <TooltipProvider>
                                                    <Tooltip delayDuration={200}>
                                                        <TooltipTrigger asChild>
                                                            <span
                                                                className={`block w-full ${count === 0 ? 'cursor-not-allowed' : ''}`}
                                                            >
                                                                <SelectFilled
                                                                    value={
                                                                        replacements[
                                                                            competency.id
                                                                        ] ?? NO_REPLACEMENT
                                                                    }
                                                                    options={selectOptions}
                                                                    onChange={(value) =>
                                                                        setReplacements((prev) => ({
                                                                            ...prev,
                                                                            [competency.id]: value,
                                                                        }))
                                                                    }
                                                                    disabled={
                                                                        isSubmitting || count === 0
                                                                    }
                                                                    sort={'none'}
                                                                    size={'sm'}
                                                                    variant={'white'}
                                                                    // Height comes from padding around the 20px chevron/line box
                                                                    // (20 + 2×7 + 2 border ≈ 36px) instead of a fixed size.
                                                                    triggerStyle={{
                                                                        height: 'auto',
                                                                    }}
                                                                    className={
                                                                        'border-gray-100 py-1.75'
                                                                    }
                                                                    placeholder={t(
                                                                        'competencies.deleteGroup.replacePlaceholder',
                                                                    )}
                                                                    dataTestId={`competency-group-replacement-${competency.id}`}
                                                                />
                                                            </span>
                                                        </TooltipTrigger>
                                                        {count === 0 && !isSubmitting && (
                                                            <TooltipContent
                                                                side={'top'}
                                                                align={'end'}
                                                                collisionPadding={8}
                                                                className={'max-w-56'}
                                                            >
                                                                <p>
                                                                    {t(
                                                                        'competencies.deleteGroup.noAssignedEmployeesTooltip',
                                                                    )}
                                                                </p>
                                                            </TooltipContent>
                                                        )}
                                                    </Tooltip>
                                                </TooltipProvider>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            }
        />
    );
};
